import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { RefObject } from 'react';

import type { ScrollingNavigationHandle } from 'components/ScrollingNavigation';

import { isClient } from 'store/utils';

import { TAB_ID } from 'constants/providerInfoTabIds';

type SectionRefs = {
  overviewRef: RefObject<HTMLDivElement>;
  roomsAndPlansRef: RefObject<HTMLDivElement>;
  providerReviewsRef: RefObject<HTMLDivElement>;
  highlightsRef: RefObject<HTMLDivElement>;
  infoFacilitiesRef: RefObject<HTMLDivElement>;
};

type Params = {
  shouldRenderAll: boolean;
  isFetching: boolean;
  sectionRefs: SectionRefs;
  scrollingNavRef: RefObject<ScrollingNavigationHandle>;
  activeTabId: string;
  isSP: boolean;
  setActiveTabId: (id: string) => void;
  setVisitedTab: (updater: (curr: Record<string, string>) => Record<string, string>) => void;
};

export default function useSectionNavigation({
  shouldRenderAll,
  isFetching,
  sectionRefs: {
    overviewRef,
    roomsAndPlansRef,
    providerReviewsRef,
    highlightsRef,
    infoFacilitiesRef,
  },
  scrollingNavRef,
  activeTabId,
  isSP,
  setActiveTabId,
  setVisitedTab,
}: Params) {
  const sectionTargetRefs = useMemo<Record<string, RefObject<HTMLDivElement>>>(
    () => ({
      [TAB_ID.OVERVIEW]: overviewRef,
      [TAB_ID.ROOMS_AND_PLANS]: roomsAndPlansRef,
      [TAB_ID.REVIEWS]: providerReviewsRef,
      [TAB_ID.HIGHLIGHTS]: highlightsRef,
      [TAB_ID.INFO]: infoFacilitiesRef,
    }),
    [overviewRef, roomsAndPlansRef, providerReviewsRef, highlightsRef, infoFacilitiesRef],
  );

  // --------------------------------------------------------------------------

  // SP -> check if different tab has been clicked
  // if so, scroll up to anchor offset
  // if same tab (prevActiveTabIdRef.current === activeTabId) ignore click (no scroll)
  const prevActiveTabIdRef = useRef(activeTabId);
  // SP -> sits above tab, used for offset calculation
  const anchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isSP || !anchorRef.current) {
      return;
    }

    // ignore scroll on initial mount, or if same tab is pressed again
    if (prevActiveTabIdRef.current === activeTabId) {
      return;
    }

    const offsetTop = anchorRef.current.offsetTop;
    // 67 = $searchForm-height-sp in providerInformation.module.scss
    // NOTE: keep in sync if that variable changes
    window.scrollTo(0, offsetTop - 67);
    prevActiveTabIdRef.current = activeTabId;
  }, [activeTabId, isSP]);

  // --------------------------------------------------------------------------

  // PC -> tracks if initial active tab has been consumed
  // not a state because we don't want to trigger a re-render when set to true
  // making it initialize to false again
  const hasConsumedInitialSectionScrollRef = useRef(false);

  // PC -> captured on the first render and saved
  // in case user interacts and modifies active tab to something else
  // this is a problem because data takes a bit of time to load
  const initialTargetIdRef = useRef<string | null>(
    isClient ? new URL(window.location.toString()).searchParams.get('activeTabId') : null,
  );

  // --------------------------------------------------------------------------

  // PC -> scroll to section captured at initialTargetIdRef once things are ready
  useEffect(() => {
    // hasConsumedInitialSectionScrollRef == true => initial scroll has been performed
    if (!isClient || hasConsumedInitialSectionScrollRef.current) {
      return;
    }

    // read activeTavId param value and check if a corresponding sections exists
    const targetId = initialTargetIdRef.current;
    if (!targetId || !sectionTargetRefs[targetId]) {
      // if not present, we mark it consumed to avoid retry
      hasConsumedInitialSectionScrollRef.current = true;
      return;
    }

    // check if ready
    // ready =
    // -> required section is mounted
    // -> scroll nav sticky bar is mounted
    // -> API has finished fetching
    // -> all sections are rendered -> sections can change height, this check is required
    if (
      !sectionTargetRefs[targetId].current ||
      !scrollingNavRef.current ||
      !shouldRenderAll ||
      isFetching
    ) {
      return;
    }

    const request = requestAnimationFrame(() => {
      // everything's good, mark consumed
      hasConsumedInitialSectionScrollRef.current = true;

      // scrollToTarget is pvt function from ScrollingNavigation component
      // lifted up through useImperativeHandle
      scrollingNavRef.current?.scrollToTarget(targetId, 'auto');
    });

    // cleanup fn in case user csr away to different page
    return () => cancelAnimationFrame(request);
  }, [
    // useEffect runs again and again until all these states are sorted
    // i.e. until API has finished fetching and such
    shouldRenderAll,
    sectionTargetRefs,
    isFetching,
    scrollingNavRef,
  ]);

  // --------------------------------------------------------------------------

  // update active tab query param
  const updateSectionQuery = useCallback((id: string) => {
    if (!isClient) {
      return;
    }
    const url = new URL(window.location.toString());
    if (url.searchParams.get('activeTabId') !== id) {
      url.searchParams.set('activeTabId', id);
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // SP -> set visited tab list related to lazy loading
  // SP -> set active tab state
  // PC -> scroll to section
  // SP & PC -> update active tab query param
  const selectSection = useCallback(
    (id: string) => {
      if (isSP) {
        setVisitedTab((curr) => ({ ...curr, [id]: id }));
        setActiveTabId(id);
      } else {
        scrollingNavRef.current?.scrollToTarget(id);
      }
      updateSectionQuery(id);
    },
    [isSP, scrollingNavRef, setActiveTabId, setVisitedTab, updateSectionQuery],
  );

  // --------------------------------------------------------------------------

  return { updateSectionQuery, selectSection, anchorRef };
}
